import React, {
	useCallback,
	useState,
	useMemo,
	useRef,
	type ReactNode,
	type Ref,
	type PropsWithChildren,
	Fragment,
} from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import { getPriorityIdFromAri } from '@jira/issue__parse-issue-url/src/id-from-ari/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { createMutationErrorHandlers } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/utils/extract-error-message.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import PriorityEditViewEntryPoint from '@atlassian/jira-issue-field-priority-editview-full/src/entrypoint.tsx';
import type {
	NullableOption,
	PriorityEditViewProps,
} from '@atlassian/jira-issue-field-priority-editview-full/src/ui/priority/types.tsx';
import { PriorityReadView } from '@atlassian/jira-issue-field-priority-readview-full/src/ui/priority/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import type { priority_issueFieldPriority_PriorityField_Mutation as PriorityMutation } from './__generated__/priority_issueFieldPriority_PriorityField_Mutation.graphql';
import type { priority_issueFieldPriorityInlineEditFull_PriorityInlineEditView_fragmentRef$key as PriorityFragment } from './__generated__/priority_issueFieldPriorityInlineEditFull_PriorityInlineEditView_fragmentRef.graphql';
import type { priority_issueFieldPriorityInlineEditFull_PriorityInlineEditViewIsEditable_fragmentRef$key as PriorityWithIsEditableFragment } from './__generated__/priority_issueFieldPriorityInlineEditFull_PriorityInlineEditViewIsEditable_fragmentRef.graphql';
import type {
	PriorityInlineEditViewProps,
	PriorityInlineEditViewWithIsEditableProps,
} from './types.tsx';
import { transformOptionToAgg } from './utils.tsx';

const isEqualPriority = (a: NullableOption, b: NullableOption) => a?.value === b?.value;

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-priority-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `PriorityInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [PriorityInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const PriorityInlineEditViewIsEditable = ({
	attributes,
	spacing = 'compact',
	editViewPopup,
	editViewPopupAlignBlock,
	fragmentRef,
	isCompact,
	menuPosition,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	isEditable,
	readViewFitContainerHeight,
	shouldRenderHoverPopover,
}: PriorityInlineEditViewWithIsEditableProps) => {
	// #region Relay
	const data = useFragment<PriorityWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment priority_issueFieldPriorityInlineEditFull_PriorityInlineEditViewIsEditable_fragmentRef on JiraPriorityField {
				...priority_issueFieldPriorityReadviewFull_PriorityReadView
				id
				fieldId
				type
				name
				priority {
					id
					name
					iconUrl
				}
			}
		`,
		fragmentRef,
	);
	const { overriding } = useInlineEditFieldInjections();
	const { id: uniqueFieldId, fieldId, type, name, priority } = data;

	const hoverPopoverRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;

	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: data.priority?.name,
		});
	}

	const fieldName = useMemo(() => overriding?.overrideLabel(name), [name, overriding]);

	const isFieldEditable = useMemo(
		() => overriding?.overrideIsEditable(isEditable),

		[isEditable, overriding],
	);

	const filterOptionsById = useMemo(
		() => overriding?.overrideFieldOptionsFilter(null),
		[overriding],
	);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<PriorityMutation>(graphql`
		mutation priority_issueFieldPriority_PriorityField_Mutation(
			$input: JiraUpdatePriorityFieldInput!
		) @raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updatePriorityField(input: $input) {
					success
					errors {
						message
					}
					field {
						priority {
							id
							name
							iconUrl
						}
					}
				}
			}
		}
	`);
	// #endregion

	const initialValue = useMemo(
		() =>
			priority
				? {
						label: priority.name ?? '',
						value: priority.id,
						iconUrl: priority.iconUrl ?? '',
					}
				: null,
		[priority],
	);

	// #region Common state
	const [updatedValue, setUpdatedValue] = useState<NullableOption>(initialValue);

	const handleSubmit = useCallback(
		(option: NullableOption, { onSuccess, onFail }: OnSubmitCallbacks) => {
			const aggValue = transformOptionToAgg(option);

			onSubmit?.(aggValue);

			if (option?.value != null) {
				const onCompleted = fg('thor_inline-edit-actions-error-extraction')
					? createMutationErrorHandlers({ onSuccess, onFail })
					: // eslint-disable-next-line @typescript-eslint/no-explicit-any
						(response: any) => {
							if (response.jira?.updatePriorityField?.success) {
								onSuccess();
							} else {
								onFail();
							}
						};

				commit({
					variables: {
						input: {
							id: uniqueFieldId,
							operation: {
								operation: 'SET',
								id: option?.value,
							},
						},
					},
					onCompleted,
					onError(error) {
						onFail(error);
					},
					optimisticResponse: {
						jira: {
							updatePriorityField: {
								success: true,
								errors: null,
								field: {
									id: uniqueFieldId,
									priority: aggValue,
								},
							},
						},
					},
				});
			}
		},
		[onSubmit, commit, uniqueFieldId],
	);

	const handleSubmitSucceeded = useCallback(
		(value: NullableOption) => {
			onSubmitSucceeded?.(transformOptionToAgg(value));
		},
		[onSubmitSucceeded],
	);

	const setAnalyticsAttributes = useCallback(
		(value: NullableOption) => {
			getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
				fieldId,
				getPriorityIdFromAri(initialValue?.value),
				getPriorityIdFromAri(value?.value),
			);
		},
		[fieldId, initialValue],
	);

	const {
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChangeAndConfirm,
		handleCopy,
		handleCut,
		handlePaste,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType: type,
		initialValue,
		isValueEqual: isEqualPriority,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded: handleSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		updatedValue,
		clipboardComponentType: 'priority',
		setAnalyticsAttributes,
	});

	const readViewContentRef = useRef<HTMLDivElement>(null);

	const renderReadView = useCallback(
		(ref: Ref<HTMLDivElement> | null = null) =>
			() => (
				<MaybeUFOSegment>
					<PriorityReadView
						fragmentRef={data}
						isCompact={isCompact}
						contentRef={readViewContentRef}
						ref={ref}
					/>
				</MaybeUFOSegment>
			),
		[data, isCompact],
	);

	const getEditViewProps = (fieldProps: ValidationFieldProps): PriorityEditViewProps => ({
		...fieldProps,
		spacing,
		fieldId: uniqueFieldId,
		fieldName,
		autoFocus: true,
		value: updatedValue,
		onChange: handleChangeAndConfirm,
		openMenuOnFocus: true,
		menuPosition,
		filterOptionsById,
	});

	const content = (ref: Ref<HTMLDivElement> | null = null): ReactNode => (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupMinWidth="small"
			editViewEntryPoint={PriorityEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={ref ? renderReadView(ref) : renderReadView()}
			hideActionButtons
		/>
	);

	if (!shouldRenderHoverPopover) return content();

	return content(hoverPopoverRef);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [PriorityInlineEditViewProps](./types.tsx)
 */

export const PriorityInlineEditView = ({
	fragmentRef,
	...props
}: PriorityInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<PriorityFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment priority_issueFieldPriorityInlineEditFull_PriorityInlineEditView_fragmentRef on JiraPriorityField {
				...priority_issueFieldPriorityInlineEditFull_PriorityInlineEditViewIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);

	return (
		<PriorityInlineEditViewIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
